import Bugsnag from '@bugsnag/browser';
import { defineStore } from 'pinia';
import { LocalStorage } from 'quasar';
import { LocalStorageKeys } from 'src/constants';
import {
	authLogin,
	passwordTokenCreate,
	PasswordTokenCreateBody,
	passwordTokenUpdate,
	PasswordTokenUpdateBody,
	passwordTokenVerify,
	PasswordTokenVerifyBody,
	userDelete,
	UserLogin,
	userPhotoUpdate,
	UserPhotoUpdateBody,
	userPushTokenUpdate,
} from 'src/lib/api';

const getInitialUserLoginState = () => {
	const initialState = LocalStorage.getItem(
		LocalStorageKeys.USER_INFO
	) as UserLogin | null;
	if (initialState) {
		Bugsnag.setUser(
			`${initialState?.data?.id}`,
			initialState?.data?.email,
			initialState?.data?.name
		);
	}
	return initialState;
};

export const useAccountStore = defineStore('account', {
	state: () => ({
		userLogin: getInitialUserLoginState(),
		pushToken: null as string | null,
	}),
	getters: {
		token(state) {
			return state.userLogin?.token;
		},
		userId(state) {
			return state.userLogin?.data?.id;
		},
	},
	actions: {
		async deleteAccount() {
			const response = await userDelete(this.userId as number, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			});

			return response;
		},
		async uploadPhoto(body: UserPhotoUpdateBody) {
			const response = await userPhotoUpdate(this.userId as number, body, {
				headers: {
					Authorization: `Bearer ${this.token}`,
				},
			});
			this.userLogin = {
				...this.userLogin,
				data: { ...response.data, notes: '' },
			};
			LocalStorage.set(LocalStorageKeys.USER_INFO, this.userLogin);

			return response.data;
		},
		async sendPasswordResetCodeToEmail(body: PasswordTokenCreateBody) {
			const response = await passwordTokenCreate(body);
			return response.data;
		},
		async verifyPasswordToken(body: PasswordTokenVerifyBody) {
			const response = await passwordTokenVerify(body);
			return response.data;
		},
		async resetPassword(body: PasswordTokenUpdateBody) {
			const response = await passwordTokenUpdate(body);
			return response.data;
		},
		async login(email: string, password: string, deviceName: string) {
			const response = await authLogin({
				device_name: deviceName,
				email: email,
				password: password,
			});

			if (this.pushToken)
				userPushTokenUpdate(
					response.data.data?.id as number,
					{
						push_token: this.pushToken,
					},
					{
						headers: {
							Authorization: `Bearer ${response.data.token}`,
						},
					}
				);

			this.userLogin = {
				...response.data,
				data: {
					...response.data?.data,
					notes: '',
				},
			};
			LocalStorage.set(LocalStorageKeys.USER_INFO, this.userLogin);

			Bugsnag.setUser(
				`${this.userLogin?.data?.id}`,
				this.userLogin?.data?.email,
				this.userLogin?.data?.name
			);
		},
		logout() {
			this.userLogin = null;
			LocalStorage.remove(LocalStorageKeys.USER_INFO);
		},
	},
});
